import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { requireAdmin, requireAuth } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { CoursesRoute } from "./routes/courses";
import { ItemsPage } from "./routes/dashboard/items";
import { ItemDetailPage } from "./routes/dashboard/items/detail";
import { SettingsCoursesPage } from "./routes/dashboard/settings/courses";
import { SettingsEmailPage } from "./routes/dashboard/settings/email";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsTrainingPage } from "./routes/dashboard/settings/training";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  component: CoursesRoute,
});

const itemsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items",
  component: ItemsPage,
});

const itemDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items/$itemId",
  component: ItemDetailPage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin(ctx);
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/courses" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsCoursesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/courses",
  component: SettingsCoursesPage,
});

const settingsTrainingRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/training",
  component: SettingsTrainingPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsEmailRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/email",
  component: SettingsEmailPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    itemsRoute,
    itemDetailRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsCoursesRoute,
      settingsTrainingRoute,
      settingsSystemRoute,
      settingsEmailRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
